import { API_URL, USER_ACTIVITY_DATA_KEY } from '../constants';
import { getCookiesString } from './app/actions';
import { getCookie, setCookie } from './cookiesControls';

function getUrlWithBase(url: string) {
  return `${API_URL}/${url}`;
}

async function checkCID() {
  if (typeof document !== 'undefined') {
    const cidCookie = getCookie('cid');

    if (!cidCookie) {
      const newUserId = crypto.randomUUID();

      const currentYear = new Date().getFullYear();
      const expiresDate = new Date().setFullYear(currentYear + 1);

      setCookie('cid', newUserId, expiresDate);
    }
  }
}

async function getDefaultHeaders() {
  const isServer = typeof window === 'undefined';
  let cookiesString = '';

  if (isServer) {
    cookiesString = await getCookiesString();
  } else {
    cookiesString = document.cookie;
  }

  const defaultHeaders = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Cookie: cookiesString,
  };

  if (typeof localStorage !== 'undefined') {
    const savedActivityData = localStorage.getItem(USER_ACTIVITY_DATA_KEY);

    if (savedActivityData) {
      const parsedData = JSON.parse(savedActivityData);

      return {
        ...defaultHeaders,
        ...(parsedData.analyticsSessionId ? { 'sapient-session-id': parsedData.analyticsSessionId } : {}),
      };
    }
  }

  return defaultHeaders;
}

const apiClient = {
  async get<ResponseType>(url: string, params?: RequestInit) {
    await checkCID();
    const defaultHeaders = await getDefaultHeaders();
    let response = await fetch(getUrlWithBase(url), {
      method: 'GET',
      headers: defaultHeaders,
      credentials: 'include',
      ...params,
    });

    if (response.status === 404) throw new Error('404 Not Found');

    return await response.json() as Promise<ResponseType>;
  },
  async getResponse<ResponseType>(url: string, params?: RequestInit) {
    await checkCID();
    const defaultHeaders = await getDefaultHeaders();
    let response = await fetch(getUrlWithBase(url), {
      method: 'GET',
      headers: defaultHeaders,
      credentials: 'include',
      ...params,
    });
    const isJson = response.headers.get('content-type')?.startsWith('application/json');

    return { status: response.status, response: (isJson ? await response.json() : response) } as { status: number, response: ResponseType };
  },
  async post<ResponseType>(url: string, params?: RequestInit) {
    const cookieReferer = getCookie('referer') || '';
    await checkCID();
    const defaultHeaders = await getDefaultHeaders();
    let response = await fetch(getUrlWithBase(url), {
      method: 'POST',
      headers: {
        ...defaultHeaders,
        Accept: 'application/json',
        'Original-Referer': cookieReferer,
        'Content-Type': 'application/json',
      },
      referrerPolicy: 'no-referrer-when-downgrade',
      credentials: 'include',
      ...params,
    });
    return await response.json() as Promise<ResponseType>;
  },
  async postResponse<ResponseType>(url: string, params?: RequestInit) {
    const cookieReferer = getCookie('referer') || '';
    checkCID();
    const defaultHeaders = await getDefaultHeaders();

    let response = await fetch(getUrlWithBase(url), {
      method: 'POST',
      headers: {
        ...defaultHeaders,
        Accept: 'application/json',
        'Original-Referer': cookieReferer,
        'Content-Type': 'application/json',
      },
      referrerPolicy: 'no-referrer-when-downgrade',
      credentials: 'include',
      ...params,
    });

    return { status: response.status, response: await response.json() } as { status: number, response: ResponseType };
  },
};

export default apiClient;
